import React from "react";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type {
  NotificationChannel,
  NotificationSection,
} from "../Types/NotificationTypes";
import {
  FALLBACK_SECTION_ICON,
  SECTION_ICONS,
} from "../Utils/NotificationConstants";
import NotificationActivityRow from "./NotificationActivityRow";
import "../Styles/NotificationSectionCard.scss";

interface NotificationSectionCardProps {
  section: NotificationSection;
  sectionIdx: number;
  expanded: boolean;
  configureTarget: { sectionIdx: number; itemIdx: number } | null;
  deletingRuleId: number | null;
  language: string;
  onToggleSection: (_idx: number) => void;
  onToggle: (_s: number, _i: number, _channel: NotificationChannel, _checked: boolean) => void;
  onConfigure: (_s: number, _i: number) => void;
  onDelete: (_s: number, _i: number) => void;
  renderConfigurePanel: (
    _section: NotificationSection,
    _item: NotificationSection["items"][number],
    _sectionIdx: number,
    _itemIdx: number
  ) => React.ReactNode;
}

const NotificationSectionCard: React.FC<NotificationSectionCardProps> = ({
  section,
  sectionIdx,
  expanded,
  configureTarget,
  deletingRuleId,
  language,
  onToggleSection,
  onToggle,
  onConfigure,
  onDelete,
  renderConfigurePanel,
}) => {
  const { t } = useTranslation();

  return (
    <div className="notification-category-card">
      <button
        type="button"
        className="notification-category-header"
        aria-expanded={expanded}
        onClick={() => onToggleSection(sectionIdx)}
      >
        <img
          src={SECTION_ICONS[section.system] ?? FALLBACK_SECTION_ICON}
          alt={section.category}
          className="category-icon"
        />
        <div className="category-info">
          <span className="category-title">
            {t(
              `notificationPreferences.categories.${section.category}.label`,
              { defaultValue: section.category }
            )}
          </span>
          <span className="category-description">{section.description}</span>
        </div>
        <span className="toggle-icon">
          {expanded ? <UpOutlined /> : <DownOutlined />}
        </span>
      </button>

      {expanded && (
        <div className="notification-table-wrapper">
          <table className="notification-table">
            <thead>
              <tr className="table-main-row">
                <th className="activity-col">
                  {t("notificationPreferences.table.activity", {
                    defaultValue: "Activity",
                  })}
                </th>
                <th className="toggle-col">
                  {t("notificationPreferences.table.email", {
                    defaultValue: "Email",
                  })}
                </th>
                <th className="toggle-col">
                  {t("notificationPreferences.table.push", {
                    defaultValue: "Push",
                  })}
                </th>
                <th className="toggle-col">
                  {t("notificationPreferences.table.inApp", {
                    defaultValue: "In-App",
                  })}
                </th>
                <th className="threshold-col">
                  {t("notificationPreferences.table.thresholdConfig", {
                    defaultValue: "Threshold Configuration",
                  })}
                </th>
              </tr>
            </thead>
            <tbody>
              {section.items.map((item, itemIdx) => {
                const isConfigOpen =
                  configureTarget?.sectionIdx === sectionIdx &&
                  configureTarget?.itemIdx === itemIdx;

                return (
                  <React.Fragment key={item.key}>
                    <NotificationActivityRow
                      item={item}
                      system={section.system}
                      sectionIdx={sectionIdx}
                      itemIdx={itemIdx}
                      isConfigOpen={isConfigOpen}
                      deletingRuleId={deletingRuleId}
                      t={t}
                      language={language}
                      onToggle={onToggle}
                      onConfigure={onConfigure}
                      onDelete={onDelete}
                    />
                    {isConfigOpen &&
                      renderConfigurePanel(section, item, sectionIdx, itemIdx)}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NotificationSectionCard;
